import React, { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { toast } from "react-toastify";
// import { Testnet } from "@nibiruchain/nibijs";
import { NUSDIcon, NibiruIcon } from "../../../AssetComponents/Images";
import {
  depositToVault,
  depositWithDiscountAndLock,
  getUserTokenBalance,
  getLockDiscount,
  getMaxDeposit,
  getCollateralizationP,
  getVaultInfo,
  getVaultApy,
  getVault30DayApy,
  getUserVaultDeposits
} from "../Vaults/VaultQueries";
// Add EVM imports
import {
  depositToVaultEvm,
  depositWithDiscountAndLockEvm,
  getCollateralErc20AddressOfVault
} from "../../../evm/services/vaultsEvmService";
import { useWalletConnection } from "../../../hooks/useWalletConnection";
import EpochInfoDisplay from "../../../Components/VaultsComponents/EpochInfoDisplay";
import { getVaultCache, setVaultCache } from "./utils/vaultCache";
import { PurpleButton } from "../../../Components/Buttons/PurpleButton";
import { BlackButton } from "../../../Components/Buttons/BlackButton";
import { WhiteButton } from "../../../Components/Buttons/WhiteButton";
import { getCollateralDenom, getContractAddresses, isTestnet } from "../../../config/network";
import { DepositFormMobile } from "./components/DepositFormMobile";
import VaultPerformanceChart from "./components/VaultPerformanceChart";
import { showTransactionToast, dismissTransactionToast } from "../../../Components/Toast/TransactionToast";
import Modal from "react-responsive-modal";
import { GreenButton } from "../../../Components/Buttons/GreenButton";
import SegmentedTabs from "../../../Components/Selection/SegmentedTabs";
import { getPerpBorrowingFees } from "../../../Components/functions/Contract";
import { ReactComponent as TokenPriceIcon } from "../../../assets/images/vaults/tokenprice.svg";
import { ReactComponent as VaultHealthIcon } from "../../../assets/images/vaults/vaulthealth.svg";
import { ReactComponent as MyDepositsIcon } from "../../../assets/images/vaults/mydeposits.svg";
import { Skeleton, SkeletonLine } from "../../../Components/utils/Skeleton";

/* Skeleton shimmer bar. A pending figure is a recessed slot the value will
   land in, not a grey rectangle floating on the card — so it takes the same
   `--surface-2` + `--inset-sm` pair as every other well, and it reads in both
   themes rather than only against white. */
const SkeletonBar = ({ width = "w-24", height = "h-6" }) => (
  <span className={`ce-skel ce-skel-line inline-block align-middle ${width} ${height}`} />
);

/* The "what is this figure" affordance the stat labels carry. */
const InfoGlyph = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    className="w-3.5 h-3.5 flex-shrink-0 hidden sm:block"
    style={{ color: "var(--ink-4)" }}
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth={2}
  >
    <circle cx="12" cy="12" r="10" />
    <path d="M12 16v-4M12 8h.01" />
  </svg>
);

/* The header's two tooltips hang off inline elements inside a `overflow`-clipped
   column, so they are positioned against the viewport rather than against the
   card. The stat tiles' own tips are absolutely positioned instead — they have
   a `relative` tile to hang from. */
const showTip = (e) => {
  const tip = e.currentTarget.querySelector(".ce-vault-tip");
  if (!tip) return;
  const rect = e.currentTarget.getBoundingClientRect();
  tip.style.position = "fixed";
  tip.style.left = `${rect.left}px`;
  tip.style.top = `${rect.bottom + 8}px`;
  tip.style.display = "block";
};
const hideTip = (e) => {
  const tip = e.currentTarget.querySelector(".ce-vault-tip");
  if (tip) tip.style.display = "none";
};

/* The three stat niches, as data rather than as three near-identical 40-line
   blocks of JSX. `align` is where the tile's tooltip hangs — the left tile
   opens right, the right tile opens left, so neither runs off the card. */
const STATS = [
  {
    key: "deposits",
    label: "My Deposits",
    Icon: MyDepositsIcon,
    align: "left",
    tip: <p>Current USD value of your SLP-USDC vault shares. Calculated as your shares multiplied by the current share price.</p>,
  },
  {
    key: "sharePrice",
    label: "Share Price",
    Icon: TokenPriceIcon,
    align: "center",
    tip: <p>How much USDC one SLP-USDC vault share is worth. This increases over time as the vault earns trading fees.</p>,
  },
  {
    key: "health",
    label: "Vault Health",
    Icon: VaultHealthIcon,
    align: "right",
    tip: (
      <>
        <p className="mb-1.5"><strong>Vault Health</strong> measures the collateralization ratio — vault assets vs liabilities.</p>
        <p className="mb-1"><span style={{ color: "var(--up)", fontWeight: 500 }}>Above 100%</span> — vault holds more than it owes. Higher = safer for LPs.</p>
        <p><span style={{ color: "var(--down)", fontWeight: 500 }}>Below 100%</span> — vault owes more than it holds (traders are in profit). Deposits may temporarily lose value.</p>
      </>
    ),
  },
];

const VaultDetail = () => {
  const { vaultId } = useParams();
  const [vault, setVault] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dataReady, setDataReady] = useState(false); // true after real data loads
  const [tokenBalance, setTokenBalance] = useState(0);
  const [depositTab, setDepositTab] = useState(0);
  const [amount, setAmount] = useState("100");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [lockDuration, setLockDuration] = useState(30 * 24 * 60 * 60);
  const [discount, setDiscount] = useState(0);
  const [maxDeposit, setMaxDeposit] = useState(Infinity);
  const [collateralizationP, setCollateralizationP] = useState("100.00");
  const [userDeposits, setUserDeposits] = useState("$0.00");
  // Bumped after a successful deposit/withdraw to force dependent data (deposits,
  // share price, chart) to re-fetch without a full page reload.
  const [refreshKey, setRefreshKey] = useState(0);

  // Bridge help modals
  const [showUsdcBridgeModal, setShowUsdcBridgeModal] = useState(false);
  const [showNibiBridgeModal, setShowNibiBridgeModal] = useState(false);
  const [hideBridgeOnConnect, setHideBridgeOnConnect] = useState(false);

  const account = useSelector((state) => state.auth.account);
  const dispatch = useDispatch();
  // Add wallet connection hook
  const { isEvm, isCosmos } = useWalletConnection();
  
  // Helper function to format large numbers with K, M, B suffixes (same as VaultsList)
  const formatLargeNumber = (num) => {
    if (num >= 1e9) {
      return `$${(num / 1e9).toFixed(2)}B`;
    } else if (num >= 1e6) {
      return `$${(num / 1e6).toFixed(2)}M`;
    } else if (num >= 1e3) {
      return `$${(num / 1e3).toFixed(2)}K`;
    } else {
      return `$${num.toFixed(2)}`;
    }
  };
  
  // Add fixed style to body to prevent horizontal scroll
  useEffect(() => {
    const originalStyle = document.body.style.overflowX;
    document.body.style.overflowX = 'hidden';
    
    return () => {
      document.body.style.overflowX = originalStyle;
    };
  }, []);

  // Load "do not show again" preference for bridge modals
  useEffect(() => {
    try {
      const stored = window.localStorage.getItem("perp_hide_bridge_on_connect");
      if (stored === "true") {
        setHideBridgeOnConnect(true);
      }
    } catch (error) {
      console.error("Error reading bridge preference from localStorage:", error);
    }
  }, []);

  const handleToggleHideBridgeOnConnect = useCallback(() => {
    setHideBridgeOnConnect((prev) => {
      const next = !prev;
      try {
        window.localStorage.setItem(
          "perp_hide_bridge_on_connect",
          next ? "true" : "false"
        );
      } catch (error) {
        console.error("Error saving bridge preference to localStorage:", error);
      }
      return next;
    });
  }, []);
  
  useEffect(() => {
    const fetchVaultDetails = async () => {
      const VAULT_CLOSING_FEE_P = 0.42;
      const cachedData = getVaultCache();

      // --- PHASE 1: Show UI instantly with cached or default values ---
      const defaultVault = {
        id: "usdc",
        name: "USDC Vault",
        icon: <NUSDIcon className="w-12 h-12" />,
        apy: cachedData?.apy || "—",
        apy30Day: cachedData?.apy30Day || "—",
        tvl: cachedData?.tvl || "—",
        description: "Deposit USDC to earn yield from platform trading fees. You receive SLP-USDC vault shares representing your ownership in the pool. As trading fees are collected, the share price increases, so your shares become worth more USDC over time.",
        vaultToken: "USDC",
        vaultTokenPrice: cachedData?.tokenPrice || "$1.00",
        collateralIndex: process.env.REACT_APP_PERP_TOKEN_INDEXES_COLLATERAL,
        epochDuration: "72 hours",
        withdrawalWindow: "48 hours",
        closingFee: cachedData?.closingFee || "—",
      };

      if (cachedData?.maxDeposit != null) setMaxDeposit(cachedData.maxDeposit);
      if (cachedData?.collateralizationP) setCollateralizationP(cachedData.collateralizationP);

      // If cache has real data, mark ready immediately (no skeleton)
      const cacheHasData = cachedData?.apy && cachedData?.tvl && cachedData?.closingFee;
      if (cacheHasData) setDataReady(true);

      setVault(defaultVault);
      setLoading(false); // UI visible immediately

      // --- PHASE 2: Fire ALL independent queries in parallel ---
      const collateralId = isTestnet() ? 3 : 1;
      const needsFresh = !cachedData;

      const promises = {
        vaultInfo: getVaultInfo(),
        vaultApy: getVaultApy(),
        vault30DayApy: getVault30DayApy(),
        borrowingFees: getPerpBorrowingFees(0, collateralId).catch(() => null),
        maxDep: getMaxDeposit().catch(() => Infinity),
        collatP: getCollateralizationP().catch(() => "100.00"),
      };

      const results = await Promise.allSettled(Object.values(promises));
      const keys = Object.keys(promises);
      const resolved = {};
      keys.forEach((key, i) => {
        resolved[key] = results[i].status === "fulfilled" ? results[i].value : null;
      });

      // Calculate TVL & share token price from fresh data or keep cached
      let formattedTVL = cachedData?.tvl || "$0.00";
      let formattedTokenPrice = cachedData?.tokenPrice || "$1.00";
      let rawSharePrice = cachedData?.rawSharePrice || 1.0;

      if (resolved.vaultInfo) {
        const tvlValue = Number(resolved.vaultInfo.tvl) / Math.pow(10, process.env.REACT_APP_USDC_DECIMALS);
        formattedTVL = formatLargeNumber(tvlValue);
        rawSharePrice = resolved.vaultInfo.sharePrice;
        formattedTokenPrice = `$${rawSharePrice.toFixed(2)}`;
      }

      // Calculate closing fee
      let vaultClosingFeePct = "0.042%";
      if (resolved.borrowingFees?.closeFeePct) {
        const vaultSharePct = resolved.borrowingFees.closeFeePct * VAULT_CLOSING_FEE_P;
        vaultClosingFeePct = `${(vaultSharePct * 100).toFixed(3)}%`;
      }

      const finalApy = resolved.vaultApy || cachedData?.apy || "6.25%";
      const final30DApy = resolved.vault30DayApy || cachedData?.apy30Day || "0.00%";
      const finalMaxDeposit = resolved.maxDep ?? Infinity;
      const finalCollatP = resolved.collatP || "100.00";

      // Update all vault state at once
      setVault({
        ...defaultVault,
        apy: finalApy,
        apy30Day: final30DApy,
        tvl: formattedTVL,
        vaultTokenPrice: formattedTokenPrice,
        rawSharePrice,
        closingFee: vaultClosingFeePct,
      });
      setMaxDeposit(finalMaxDeposit);
      setCollateralizationP(finalCollatP);
      setDataReady(true); // Real data loaded — hide skeletons

      // Persist to cache for next navigation
      setVaultCache(formattedTVL, formattedTokenPrice, {
        apy: finalApy,
        apy30Day: final30DApy,
        rawSharePrice,
        closingFee: vaultClosingFeePct,
        maxDeposit: finalMaxDeposit,
        collateralizationP: finalCollatP,
      });

      // --- PHASE 3: User-specific data (non-blocking) ---
      if (account) {
        try {
          const collateralDenom = getCollateralDenom();
          const [balance, deposits] = await Promise.all([
            getUserTokenBalance(account, collateralDenom, isEvm),
            getUserVaultDeposits(account, isEvm),
          ]);
          setTokenBalance(balance);
          if (deposits && deposits.length > 0) {
            const totalDeposits = deposits.reduce((total, deposit) => total + parseFloat(deposit.currentValue), 0);
            setUserDeposits(`$${totalDeposits.toFixed(2)}`);
          } else {
            setUserDeposits("$0.00");
          }
        } catch (err) {
          console.error("Error fetching user-specific vault data:", err);
        }
      } else {
        setUserDeposits("$0.00");
      }
    };

    fetchVaultDetails();
  }, [vaultId, account, isEvm, refreshKey]);
  
  useEffect(() => {
    const calculateDiscount = async () => {
      if (depositTab === 1) {
        const discountPercent = await getLockDiscount(lockDuration);
        setDiscount(parseFloat(discountPercent));
      }
    };
    
    calculateDiscount();
  }, [lockDuration, depositTab]);
  
  const handleAmountChange = (e) => {
    const value = e.target.value;
    
    // Allow empty input
    if (value === '') {
      setAmount('');
      return;
    }
    
    // Only allow numbers and one decimal point
    if (!/^\d*\.?\d*$/.test(value)) {
      return;
    }
    
    // Prevent multiple decimal points
    if ((value.match(/\./g) || []).length > 1) {
      return;
    }
    
    // Validate against max deposit and balance
    if (value && parseFloat(value) > maxDeposit) {
      setAmount(maxDeposit.toString());
      toast.info(`Amount limited to maximum deposit of ${maxDeposit} USDC`);
    } else if (tokenBalance && value && parseFloat(value) > parseFloat(tokenBalance)) {
      setAmount(tokenBalance.toString());
    } else {
      setAmount(value);
    }
  };
  
  const handleMaxClick = () => {
    setAmount(Math.min(tokenBalance, maxDeposit).toString());
  };
  

  // Internal deposit handler that accepts an amount parameter
  const executeDeposit = async (depositAmount) => {
    if (!account) {
      toast.error("Please connect your wallet");
      return false;
    }
    
    if (!depositAmount || parseFloat(depositAmount) <= 0) {
      toast.error("Please enter a valid amount");
      return false;
    }
    
    if (!vault) {
      toast.error("Vault information not loaded. Please try again.");
      return false;
    }
    
    setIsSubmitting(true);
    showTransactionToast("Processing deposit, please wait...");
    
    try {
      let success = false;
      
      if (isEvm) {
        // Get network-aware vault contract address
        const vaultAddress = getContractAddresses().vault;
        if (!vaultAddress) {
          throw new Error("Vault contract address not configured");
        }
        
        // Get collateral address for EVM
        const collateralAddress = await getCollateralErc20AddressOfVault(vaultAddress);
        
        // Prepare amounts
        const depositAmountBank = Math.floor(
          parseFloat(depositAmount) * (10 ** process.env.REACT_APP_USDC_DECIMALS)
        ).toString();
        
        const useErc20Amount = Math.floor(
          parseFloat(depositAmount) * (10 ** process.env.REACT_APP_USDC_DECIMALS)
        ).toString();
        
        if (depositTab === 0) {
          // Standard deposit
          const result = await depositToVaultEvm({
            vaultAddress: vaultAddress,
            depositAmount: depositAmountBank,
            useErc20Amount: useErc20Amount,
            collateralAddress: collateralAddress,
            sendSharesToEvm: true
          });
          success = result.success;
        } else {
          // Locked deposit - show toast for EVM mode
          toast.warning("Locked deposits are not available in EVM mode yet. Please use a Cosmos wallet for this function.");
          setIsSubmitting(false);
          return false;
        }
      } else if (isCosmos) {
        console.log("Using Cosmos integration for deposit");
        
        if (depositTab === 0 && vault) {
          // Standard deposit
          success = await depositToVault(
            account,
            depositAmount,
            dispatch,
            vault.collateralIndex
          );
        } else {
          toast.warning("Locked deposits are not available anymore. Please use Standard Deposit only.");
          setIsSubmitting(false);
          return false;
        }
      } else {
        toast.error("Please connect a supported wallet");
        return false;
      }
      
      if (success) {
        setAmount("");

        // Read-after-write: the tx is confirmed, but the chain node often hasn't
        // indexed the new vault state yet, so an immediate re-query returns the OLD
        // deposit value (which is why a manual reload "fixed" it). Poll a few times
        // until the deposits value changes, then bump refreshKey so the stat tiles,
        // share price and chart all re-fetch too.
        const prevDeposits = userDeposits;
        const collateralDenom = getCollateralDenom();

        const refreshDeposits = async () => {
          const balance = await getUserTokenBalance(account, collateralDenom, isEvm);
          setTokenBalance(balance);
          const deposits = await getUserVaultDeposits(account, isEvm);
          let label = "$0.00";
          if (deposits && deposits.length > 0) {
            const totalDeposits = deposits.reduce((t, d) => t + parseFloat(d.currentValue), 0);
            label = `$${totalDeposits.toFixed(2)}`;
          }
          setUserDeposits(label);
          return label;
        };

        // Try immediately, then retry with backoff until the value updates.
        let updated = await refreshDeposits();
        for (let i = 0; i < 5 && updated === prevDeposits; i++) {
          await new Promise((r) => setTimeout(r, 1500));
          updated = await refreshDeposits();
        }

        // Re-fetch vault-level data (share price, TVL, health) and the chart.
        setRefreshKey((k) => k + 1);

        dismissTransactionToast();
        // toast.success("Deposit successful!");
        return true;
      }
      dismissTransactionToast();
      return false;
    } catch (error) {
      console.error("Error in executeDeposit:", error);
      dismissTransactionToast();
      toast.error(`Deposit failed: ${error.message}`);
      return false;
    } finally {
      setIsSubmitting(false);
    }
  };

  // Updated handleDeposit function - uses the amount state
  const handleDeposit = async () => {
    await executeDeposit(amount);
  };
  
  const getExpectedVaultTokens = () => {
    if (!amount) return "0.00";

    const sharePrice = vault.rawSharePrice || 1.0;
    let inputAmount = parseFloat(amount);
    if (depositTab === 1 && discount > 0) {
      inputAmount = inputAmount * (1 + discount);
    }

    const expectedTokens = inputAmount / sharePrice;

    return expectedTokens.toFixed(6);
  };
  
  const getDurationOptions = () => [
    { value: 30 * 24 * 60 * 60, label: "30 Days" },
    { value: 90 * 24 * 60 * 60, label: "90 Days" },
    { value: 180 * 24 * 60 * 60, label: "180 Days" },
    { value: 365 * 24 * 60 * 60, label: "1 Year" }
  ];
  
  if (loading) {
    // The vault page, pending. Header, three stat niches, chart and the
    // deposit ticket at their real sizes — a spinner on an empty card gives
    // the reader nothing to anticipate, and the whole layout snaps in at once
    // when the chain answers.
    return (
      <div className="w-full overflow-x-hidden vault-container" style={{ fontFamily: "General Sans" }}>
        <div className="ce-vault-card w-full max-w-full p-4 md:p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
            <div className="md:col-span-2">
              {/* Header: icon plate, name, APY chip + TVL */}
              <div className="flex items-center gap-3 mb-5">
                <Skeleton w={44} h={44} radius="var(--r-inner, 14px)" />
                <div className="flex-1 min-w-0">
                  <SkeletonLine w={180} h={24} />
                  <div className="flex items-center gap-3 mt-2">
                    <SkeletonLine w={96} h={18} index={1} />
                    <SkeletonLine w={120} h={16} index={2} />
                  </div>
                </div>
              </div>

              {/* The three stat niches */}
              <div className="grid grid-cols-3 gap-3 md:gap-4 mb-6">
                {[0, 1, 2].map((i) => (
                  <div key={i} className="ce-vault-tile min-h-[100px] md:min-h-[112px]">
                    <div className="flex items-center gap-2">
                      <Skeleton w={28} h={28} radius="var(--r-control)" index={i} />
                      <SkeletonLine w={72} h={10} index={i} />
                    </div>
                    <div className="mt-auto">
                      <SkeletonLine w={96} h={20} index={i} />
                    </div>
                  </div>
                ))}
              </div>

              {/* Performance chart */}
              <div className="ce-vault-panel p-4 mb-6">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <SkeletonLine w={88} h={10} />
                    <SkeletonLine w={128} h={22} style={{ marginTop: 8 }} />
                  </div>
                  <div className="flex items-center gap-2">
                    <SkeletonLine w={128} h={30} index={1} />
                    <SkeletonLine w={96} h={30} index={2} />
                  </div>
                </div>
                <Skeleton h={260} radius="var(--r-inner)" />
              </div>

              {/* About + epoch panel */}
              <div className="mb-6">
                <SkeletonLine w={128} h={10} />
                <SkeletonLine w="100%" h={12} style={{ marginTop: 10 }} />
                <SkeletonLine w="88%" h={12} style={{ marginTop: 8 }} />
                <SkeletonLine w="64%" h={12} style={{ marginTop: 8 }} />
              </div>
              <Skeleton h={120} radius="var(--r-panel)" />
            </div>

            {/* The deposit ticket */}
            <div className="hidden md:block md:col-span-1 w-full">
              <div className="ce-vault-card w-full p-4">
                <div className="flex justify-between items-center mb-4">
                  <SkeletonLine w={124} h={16} />
                  <SkeletonLine w={104} h={13} index={1} />
                </div>
                <SkeletonLine w={92} h={10} />
                <SkeletonLine w="100%" h={40} style={{ marginTop: 8 }} index={1} />
                <Skeleton h={52} radius="var(--r-inner)" style={{ marginTop: 12 }} index={2} />
                <Skeleton h={44} radius="var(--r-inner)" style={{ marginTop: 12 }} index={3} />
                <SkeletonLine w="100%" h={50} style={{ marginTop: 16 }} index={4} />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
  
  const statValues = {
    deposits: userDeposits,
    sharePrice: vault.vaultTokenPrice,
    health: `${collateralizationP}%`,
  };

  return (
    <div className="w-full overflow-x-hidden vault-container" style={{ fontFamily: 'General Sans' }}>
      <div className="ce-vault-card w-full max-w-full p-4 md:p-6">
        {/* <div className="mb-6">
          <button
            onClick={() => navigate('/perps/vaults')}
            className="flex items-center text-gray-600 hover:text-gray-900 transition-colors"
          >
            <ArrowToLeft className="w-4 h-4 mr-2" />
            Back to Vaults
          </button>
        </div> */}
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
          <div className="md:col-span-2">
            {/* Vault header: icon, name, APY badge, TVL.

                The APY was a `bg-green-50` lozenge and the TVL a grey caption;
                both are figures, so both become the system's objects — an up
                chip and an eyebrow-over-figure pair. */}
            <div className="flex items-center gap-3 mb-5">
              <div className="flex-shrink-0">{vault.icon}</div>
              <div className="flex-1 min-w-0">
                <h1 className="text-xl md:text-2xl font-semibold truncate" style={{ color: "var(--ink)" }}>
                  {vault.name}
                </h1>
                <div className="flex items-center gap-3 mt-1.5 flex-wrap">
                  <div
                    className="ce-chip ce-chip-up cursor-help"
                    onMouseEnter={showTip}
                    onMouseLeave={hideTip}
                  >
                    {dataReady ? `${vault.apy} APY` : <SkeletonBar width="w-14" height="h-4" />}
                    <div className="ce-vault-tip" style={{ display: "none" }}>
                      <p><strong>APY</strong> (Annual Percentage Yield) — the projected yearly return for LPs based on trading fees earned by the vault.</p>
                    </div>
                  </div>
                  <div
                    className="text-sm cursor-help ce-tabular"
                    style={{ color: "var(--ink-3)" }}
                    onMouseEnter={showTip}
                    onMouseLeave={hideTip}
                  >
                    TVL:{" "}
                    <span className="font-semibold" style={{ color: "var(--ink)" }}>
                      {dataReady ? vault.tvl : <SkeletonBar width="w-16" height="h-4" />}
                    </span>
                    <div className="ce-vault-tip" style={{ display: "none" }}>
                      <p><strong>TVL</strong> (Total Value Locked) — the total amount of USDC deposited in this vault by all liquidity providers.</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Stat niches: My Deposits, Share Price, Vault Health. Recessed,
                because you read them — you do not press them. */}
            <div className="grid grid-cols-3 gap-3 md:gap-4 mb-6">
              {STATS.map(({ key, label, Icon, tip, align }) => (
                <div
                  key={key}
                  className="ce-vault-tile relative cursor-help min-h-[100px] md:min-h-[112px]"
                  onMouseEnter={(e) => { e.currentTarget.querySelector(".ce-vault-tip").style.display = "block"; }}
                  onMouseLeave={(e) => { e.currentTarget.querySelector(".ce-vault-tip").style.display = "none"; }}
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <div className="ce-vault-tile-icon">
                      <Icon />
                    </div>
                    <span className="ce-eyebrow flex items-center gap-1 min-w-0 flex-wrap">
                      {label}
                      <InfoGlyph />
                    </span>
                  </div>
                  <div
                    className="ce-vault-tip absolute top-full mt-1"
                    style={{ display: "none", left: align === "right" ? "auto" : align === "center" ? "50%" : 0, right: align === "right" ? 0 : "auto", transform: align === "center" ? "translateX(-50%)" : "none" }}
                  >
                    {tip}
                  </div>
                  <div className="mt-auto ce-figure" style={{ fontWeight: 600, fontSize: 20, color: "var(--ink)" }}>
                    {dataReady ? statValues[key] : <SkeletonBar width="w-16" height="h-6" />}
                  </div>
                </div>
              ))}
            </div>

            {/* Mobile Deposit Form - Only visible on mobile */}
            {vault && (
              <DepositFormMobile
                vault={vault}
                tokenBalance={tokenBalance}
                maxDeposit={maxDeposit}
                onDepositWithAmount={executeDeposit}
                isConnected={!!account}
                isSubmitting={isSubmitting}
              />
            )}
            
            {/* Performance chart: share price / deposits / volume / withdrawals over time */}
            <VaultPerformanceChart vaultAddress={getContractAddresses().vault} refreshKey={refreshKey} />

            <div className="mb-6 overflow-hidden">
              <div className="ce-eyebrow mb-2">About this Vault</div>
              <p className="break-words overflow-hidden" style={{ color: "var(--ink-2)", lineHeight: 1.55 }}>
                {vault.description}
              </p>
            </div>

            {/* The epoch panel draws its own surface — it was double-boxed
                inside a hairline wrapper, which is the one thing this system
                forbids: nothing gets both a pillow and a border. */}
            <div className="mt-6 w-full overflow-hidden">
              <EpochInfoDisplay />
            </div>

            {/* Locked Deposits section — disabled until feature is ready */}
          </div>
          
          <div className="hidden md:block md:col-span-1 overflow-hidden w-full">
            {/* The deposit ticket. Same object as the order ticket on a market
                page (`TradingForm`): a card at `--card`, holding recessed wells
                for what you type into and read, and one raised submit. */}
            <div className="ce-vault-card overflow-hidden w-full p-4" style={{ fontFamily: 'General Sans' }}>
              <div className="flex justify-between items-center mb-4 gap-2">
                <h2 style={{ fontWeight: 600, fontSize: 15, color: 'var(--ink)' }}>Deposit Amount</h2>
                <span className="ce-tabular" style={{ fontWeight: 500, fontSize: 13, color: 'var(--ink-3)' }}>
                  Balance: {account ? tokenBalance : "0.00"} USDC
                </span>
              </div>

              <div className="mb-5">
                <div className="ce-eyebrow mb-2">Deposit Type</div>
                <SegmentedTabs
                  label="Deposit type"
                  grow
                  size={40}
                  value={depositTab}
                  onChange={setDepositTab}
                  items={[
                    { value: 0, label: "Standard" },
                    {
                      value: 1,
                      label: "Locked",
                      disabled: true,
                      badge: <span className="ce-chip ce-chip-accent" style={{ marginLeft: 6 }}>Soon</span>,
                    },
                  ]}
                />
              </div>
              
              <div className="mb-3">
                <div
                  className="ce-field flex items-center justify-between"
                  style={{
                    height: 52,
                    borderRadius: 14,
                    border: 0,
                    paddingLeft: 16,
                    paddingRight: 16,
                    background: 'var(--surface-2)',
                    boxShadow: 'var(--inset)',
                  }}
                >
                  <input
                    type="text"
                    value={amount}
                    onChange={handleAmountChange}
                    placeholder="0.00"
                    disabled={!account || isSubmitting}
                    className="bg-transparent outline-none w-full"
                    style={{ fontFamily: 'General Sans', fontWeight: 700, fontSize: '26px', lineHeight: '135%', letterSpacing: '-.02em', fontVariantNumeric: 'tabular-nums', color: 'var(--ink)' }}
                  />
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <button
                      type="button"
                      onClick={handleMaxClick}
                      disabled={!account || isSubmitting || !tokenBalance}
                      style={{ fontFamily: 'General Sans', fontWeight: 600, fontSize: 13, color: 'var(--cat-accent, #5B1DEE)', background: 'transparent' }}
                      className="hover:opacity-80 disabled:opacity-50"
                    >
                      MAX
                    </button>
                    <div className="ce-pill flex items-center gap-1.5" style={{ padding: '5px 10px' }}>
                      <NUSDIcon className="w-4 h-4" />
                      <span style={{ fontFamily: 'General Sans', fontWeight: 600, fontSize: 13, color: 'var(--ink-2, #344054)' }}>USDC</span>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* {depositTab === 1 && (
                <div className="mb-5 overflow-hidden">
                  <label className="block text-sm text-gray-700 mb-2">Lock Duration</label>
                  <div className="grid grid-cols-2 gap-2">
                    {getDurationOptions().map((option, index) => (
                      <div 
                        key={option.value}
                        onClick={() => setLockDuration(option.value)}
                        className={`relative flex flex-col items-center justify-center p-3 border rounded-lg cursor-pointer transition-all ${
                          lockDuration === option.value 
                            ? "border-purple-500 bg-purple-50" 
                            : "border-gray-200 hover:bg-gray-50"
                        }`}
                      >
                        <div className={`font-medium ${lockDuration === option.value ? "text-purple-700" : "text-gray-700"}`}>
                          {option.label}
                        </div>
                        {lockDuration === option.value && (
                          <div className="absolute -top-1 -right-1 w-4 h-4 bg-purple-600 rounded-full flex items-center justify-center">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 text-white" viewBox="0 0 20 20" fill="currentColor">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                  {discount > 0 && (
                    <div className="mt-3 text-sm text-center">
                      <span className="bg-green-100 text-green-800 font-medium px-2.5 py-1 rounded">
                        Your bonus: +{(discount * 100).toFixed(2)}% more tokens
                      </span>
                    </div>
                  )}
                </div>
              )} */}
              
              {/* The summary is reference, so it is recessed rather than
                  raised: you read it, you do not press it. */}
              <div className="ce-vault-well mb-4 overflow-hidden" style={{ padding: '2px 14px' }}>
                <div className="ce-vault-rows ce-tabular">
                  <div className="flex justify-between items-center gap-3">
                    <span style={{ fontWeight: 500, fontSize: 13, color: 'var(--ink-2)' }}>You will receive</span>
                    <span style={{ fontWeight: 600, fontSize: 14, color: 'var(--up)' }}>{getExpectedVaultTokens()} SLP-USDC</span>
                  </div>
                  <div className="flex justify-between items-center gap-3">
                    <span style={{ fontWeight: 500, fontSize: 13, color: 'var(--ink-2)' }}>Exchange rate</span>
                    <span style={{ fontWeight: 600, fontSize: 13, color: 'var(--cat-accent, #5B1DEE)' }}>
                      1 USDC = {((1 / (vault.rawSharePrice || 1.0)) * (depositTab === 1 ? 1 + discount : 1)).toFixed(6)} SLP-USDC
                    </span>
                  </div>
                </div>
              </div>
              
              {/* The submit leads. It was second, under an accent-filled
                  "Get USDC on Nibiru" — so the brightest control on the ticket
                  was the one that leaves the app. The bridge helper stays, as
                  the secondary it is. */}
              <div className="w-full space-y-3">
                <GreenButton
                  className="ce-btn-block"
                  text={
                    isSubmitting 
                      ? "Processing..." 
                      : !account 
                        ? "Connect Wallet" 
                        : depositTab === 0
                          ? "Deposit USDC"
                          : `Deposit & Lock USDC for ${getDurationOptions().find(option => option.value === lockDuration)?.label}`
                  }
                  onClick={handleDeposit}
                  disabled={!account || isSubmitting || !amount || parseFloat(amount) <= 0}
                />

                <WhiteButton
                  className="ce-btn-block"
                  text={
                    <div className="flex items-center justify-center gap-2">
                      Get USDC on Nibiru
                      <NibiruIcon />
                    </div>
                  }
                  onClick={() => {
                    setShowUsdcBridgeModal(true);
                  }}
                />
              </div>
              
              <div className="mt-4 overflow-hidden" style={{ fontSize: 12, lineHeight: 1.5, color: 'var(--ink-3)' }}>
                <p>Depositing assets into this vault represents your agreement to the platform.</p>
                {depositTab === 1 && (
                  <p className="mt-2">
                    <strong>Note:</strong> Locked deposits cannot be withdrawn until the lock period expires.
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bridge helper modals */}
      <Modal
        open={showUsdcBridgeModal}
        onClose={() => {
          setShowUsdcBridgeModal(false);
        }}
        center
        closeOnOverlayClick={true}
        closeOnEsc={true}
        classNames={{
          modal:
            "relative max-w-[420px] w-[95vw] md:max-w-[420px] md:w-[95vw] p-4 md:p-6 mx-auto max-h-[90vh] overflow-y-auto",
        }}
      >
        <div className="space-y-2 md:space-y-4">
          <h2 className="text-base md:text-xl font-semibold flex items-center gap-1.5 md:gap-2" style={{ color: "var(--ink)" }}>
            <NibiruIcon className="w-4 h-4 md:w-5 md:h-5" />
            <span>Get USDC on Nibiru</span>
          </h2>
          <p className="text-xs md:text-sm leading-tight md:leading-normal" style={{ color: "var(--ink-2)" }}>
            Follow these steps to bridge USDC to Nibiru using the official Nibiru Bridge.
          </p>
          <ol className="space-y-1.5 md:space-y-3 text-xs md:text-sm list-decimal list-inside leading-relaxed" style={{ color: "var(--ink-2)" }}>
            <li>
              Click <span className="font-semibold">"Open Nibiru Bridge"</span> below. This opens the Nibiru bridge in a new tab.
            </li>
            <li>
              In the bridge, set <span className="font-semibold">Supported Asset</span> to <span className="font-semibold">USD Coin (USDC.e)</span>.
            </li>
            <li>
              Choose your <span className="font-semibold">Source Chain</span> (where your USDC is) and set the <span className="font-semibold">Destination Chain</span> to <span className="font-semibold">Nibiru</span>.
            </li>
            <li>
              Enter the <span className="font-semibold">amount</span> of USDC you want to send and your <span className="font-semibold">Nibiru receiving address</span> (your wallet address).
            </li>
            <li>
              Click <span className="font-semibold">"Bridge"</span>, confirm the transaction in your wallet, and wait 5–10 minutes for funds to arrive on Nibiru.
            </li>
            <li>
              Once USDC arrives on Nibiru, you’re ready to trade on <span className="font-semibold">CodedEState</span>.
            </li>
          </ol>
          <div className="ce-vault-well p-3 text-[11px] md:text-xs" style={{ color: "var(--ink-2)" }}>
            <p className="font-semibold mb-0.5 md:mb-1">Tip:</p>
            <p className="leading-tight">
              Make sure you have a small amount of gas on your source chain to pay for the bridge transaction.
              Nibiru Bridge also provides free NIBI tokens for gas, so you’re covered once your funds arrive.
            </p>
          </div>
          <div className="flex items-center gap-1.5 md:gap-2 pt-0.5 md:pt-1">
            <input
              id="bridge-hide-on-connect-usdc"
              type="checkbox"
              checked={hideBridgeOnConnect}
              onChange={handleToggleHideBridgeOnConnect}
              className="h-3.5 w-3.5 md:h-4 md:w-4 rounded flex-shrink-0"
              style={{ accentColor: "var(--cat-accent, #5B1DEE)" }}
            />
            <label
              htmlFor="bridge-hide-on-connect-usdc"
              className="text-[10px] md:text-xs cursor-pointer leading-tight" style={{ color: "var(--ink-3)" }}
            >
              Do not show this again when I connect my wallet
            </label>
          </div>
          <div className="flex flex-col sm:flex-row gap-2 md:gap-3 pt-1 md:pt-2">
            <PurpleButton
              text="Open Nibiru Bridge"
              className="w-full sm:w-auto text-xs md:text-sm px-3 md:px-[14px] py-2 md:py-[8px]"
              onClick={() => {
                window.open("https://app.nibiru.fi/bridge", "_blank", "noopener,noreferrer");
              }}
            />
            {/* <WhiteButton
              text="Next: Get NIBI Gas"
              className="w-full sm:w-auto text-xs md:text-sm font-medium text-gray-700 px-3 md:px-[14px] py-2 md:py-[8px]"
              onClick={() => {
                setShowUsdcBridgeModal(false);
                setShowNibiBridgeModal(true);
              }}
            /> */}
          </div>
        </div>
      </Modal>

      {/* <Modal
        open={showNibiBridgeModal}
        onClose={() => setShowNibiBridgeModal(false)}
        center
        closeOnOverlayClick={true}
        closeOnEsc={true}
        classNames={{
          modal:
            "relative max-w-[420px] w-[95vw] md:max-w-[420px] md:w-[95vw] p-4 md:p-6 mx-auto max-h-[90vh] overflow-y-auto",
        }}
      >
        <div className="space-y-2 md:space-y-4">
          <h2 className="text-base md:text-xl font-semibold flex items-center gap-1.5 md:gap-2" style={{ color: "var(--ink)" }}>
            <NibiruIcon className="w-4 h-4 md:w-5 md:h-5" />
            <span>Get NIBI Gas on Nibiru</span>
          </h2>
          <p className="text-xs md:text-sm leading-tight md:leading-normal" style={{ color: "var(--ink-2)" }}>
            You need NIBI tokens to pay for gas fees when trading. Here are two easy ways to get NIBI:
          </p>
          
          <div className="bg-green-50 border border-green-200 rounded-lg p-2 md:p-3 mb-2 md:mb-4">
            <p className="text-xs md:text-sm font-semibold text-green-800 mb-1 md:mb-2">Easiest Option:</p>
            <p className="text-[10px] md:text-xs text-green-700 leading-tight">
              When bridging USDC through Stargate, you can automatically add up to 40 NIBI tokens for gas. Just click "Add more gas" if you see the warning message. This way you get both USDC and NIBI in one transaction!
            </p>
          </div>

          <p className="text-xs md:text-sm font-semibold text-gray-700 mb-1 md:mb-2">Or bridge NIBI separately:</p>
          <ol className="space-y-1 md:space-y-2 text-xs md:text-sm text-gray-700 list-decimal list-inside leading-relaxed">
            <li>
              Click <span className="font-semibold">"Open Stargate Bridge"</span> below.
            </li>
            <li>
              <span className="font-semibold">Select your source chain</span> - Choose where you have NIBI tokens.
            </li>
            <li>
              <span className="font-semibold">Select NIBI</span> as the token to bridge.
            </li>
            <li>
              <span className="font-semibold">Set Nibiru</span> as the destination and enter a small amount (0.1-1 NIBI is enough for many transactions).
            </li>
            <li>
              Review and confirm the transaction. Once it completes, you'll have NIBI for gas fees.
            </li>
          </ol>
          <div className="flex items-center gap-1.5 md:gap-2 pt-0.5 md:pt-1">
            <input
              id="bridge-hide-on-connect-nibi"
              type="checkbox"
              checked={hideBridgeOnConnect}
              onChange={handleToggleHideBridgeOnConnect}
              className="h-3.5 w-3.5 md:h-4 md:w-4 rounded flex-shrink-0"
              style={{ accentColor: "var(--cat-accent, #5B1DEE)" }}
            />
            <label
              htmlFor="bridge-hide-on-connect-nibi"
              className="text-[10px] md:text-xs text-gray-600 cursor-pointer leading-tight"
            >
              Do not show this again when I connect my wallet
            </label>
          </div>
          <div className="flex flex-col sm:flex-row gap-2 md:gap-3 pt-1 md:pt-2">
            <BlackButton
              text="Open Stargate Bridge"
              className="w-full sm:w-auto text-xs md:text-sm px-3 md:px-[14px] py-2 md:py-[8px]"
              onClick={() => {
                window.open("https://stargate.finance/?dstChain=nibiru&dstToken=0x0829F361A05D993d5CEb035cA6DF3446b060970b", "_blank", "noopener,noreferrer");
              }}
            />
            <WhiteButton
              text="Close"
              className="w-full sm:w-auto text-xs md:text-sm font-medium text-gray-700 px-3 md:px-[14px] py-2 md:py-[8px]"
              onClick={() => setShowNibiBridgeModal(false)}
            />
          </div>
        </div>
      </Modal> */}

    </div>
  );
};

export default VaultDetail;